import { useMemo } from "react";
import { getVariantPrice } from "@/helpers/pdpHelpers/priceHelpers";

/**
 * Custom hook to select the default product variant.
 * - If objectId is present in the query, selects the matching variant.
 * - Otherwise, selects the first variant with a non-zero price.
 * - Falls back to the first variant in the list.
 * @param {Array} variants - Array of variant edges from Shopify.
 * @param {object} router - Next.js router object.
 * @returns {Object} The selected first variant node.
 */
export function useFirstVariant(variants, router, setHighlightColor) {
  return useMemo(() => {
    // Only execute when router is ready
    if (!router?.isReady || !variants || !variants.length) return variants[0].node;

    // 1. If objectId or variant is present in the query, select that variant
    const variantParam = router.query.objectId || router.query.variant;
    if (variantParam) {
      const found = variants.find(vari => {
        const nodeId = vari.node.id;
        // For 'variant', decode base64 if needed, else match as with objectId
        if (router.query.variant) {
          // Shopify variant GID is base64 encoded in some URLs, handle both
          try {
            const decoded = atob(nodeId).split("/").pop();
            return (
              nodeId === variantParam ||
              nodeId.split("/").pop() === variantParam ||
              decoded === variantParam
            );
          } catch {
            // Fallback if atob fails
            return (
              nodeId === variantParam ||
              nodeId.split("/").pop() === variantParam
            );
          }
        }
        // For objectId, match as before
        return (
          nodeId === variantParam ||
          nodeId.split("/").pop() === variantParam
        );
      });
      if (found) 
        {
          setHighlightColor(true)
          return found.node;
        }
    }

    // 2. Otherwise, select the first variant with a non-zero price
    for (const vari of variants) {
      if (vari?.node) {
        const variantPrice = getVariantPrice(vari.node);
        const priceToCheck = variantPrice?.returnedSalePrice
          ? variantPrice.returnedSalePrice
          : variantPrice.returnedPrice;
        if (priceToCheck !== "$0") {
          setHighlightColor(true)
          return vari.node;
        }
      }
    }

    // 3. Fallback: return the first variant
    setHighlightColor(true)
    return variants[0].node;
  }, [variants, router?.isReady, router?.query?.objectId, router?.query?.variant]);
}